import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, Ref, computed, ComputedRef, watch } from "vue";
import { Channel } from "@/websocket/model/messages/channel";
import useOpenbetWs from "@/websocket/composables/use-openbet-ws";
import { useTicketStore } from "@/ticket/stores/use-ticket-store";
import { unionWith as _unionWith } from "lodash";
import { newChannels } from "@/websocket/utils/messages";
import { ChannelType } from "@/websocket/model/enums/channel-type";

interface ChannelDelta {
    added: Channel[];
    removed: Channel[];
    common: Channel[];
}

// Simplified equality check for channels
const isEqualChannel = (a: Channel, b: Channel) => a.id === b.id && a.type === b.type;

// Finds difference between two Channel arrays
const findChannelDelta = (channelsOriginal: Channel[], channelsNew: Channel[]): ChannelDelta => {
    enum State {
        Added = "added",
        Removed = "removed",
        Common = "common",
    }
    const uniqueProp = (i: Channel) => `${i.id}_${i.type}`;
    const delta: ChannelDelta = {
        added: [],
        removed: [],
        common: [],
    };
    const deltaMap = {};
    const objHolder = {};
    channelsOriginal.forEach((item) => {
        objHolder[uniqueProp(item)] = item;
        deltaMap[uniqueProp(item)] = State.Removed;
    });
    channelsNew.forEach((item) => {
        objHolder[uniqueProp(item)] = item;
        const d = deltaMap[uniqueProp(item)];
        if (Boolean(d) && d === State.Removed) {
            deltaMap[uniqueProp(item)] = State.Common;
        } else {
            deltaMap[uniqueProp(item)] = State.Added;
        }
    });
    Object.keys(deltaMap).forEach((id) => {
        if (deltaMap[id] === State.Removed) delta.removed.push(objHolder[id]);
        else if (deltaMap[id] === State.Added) delta.added.push(objHolder[id]);
        else if (deltaMap[id] === State.Common) delta.common.push(objHolder[id]);
    });
    return delta;
};

export const useWsStore = defineStore("Ws", () => {
    const { subscribeChannels, unsubscribeChannels } = useOpenbetWs();
    const ticketStore = useTicketStore();
    // state
    const subscribedChannels: Ref<Channel[]> = ref([]);
    // getters
    const getSubscribedChannels: ComputedRef<Channel[]> = computed(() => subscribedChannels.value);
    // actions
    // Keep in mind that if channels will be empty array previously subscribed channels will be unsubscribed
    const setSubscribedChannels = (channels: Channel[]) => {
        subscribedChannels.value = channels;
    };
    const setSubscribedChannelsByEventIds = (eventIds: string[] | number[]) => {
        const channels = !eventIds.length
            ? []
            : _unionWith(
                  newChannels(
                      ChannelType.EvallDescendants,
                      eventIds.map((id) => Number(id)),
                  ),
                  newChannels(
                      ChannelType.EventDescendants,
                      eventIds.map((id) => Number(id)),
                  ),
                  isEqualChannel,
              );
        setSubscribedChannels(channels);
    };
    const reset = (all: boolean = true) => {
        if (all) {
            $reset();
        } else {
            subscribedChannels.value = subscribedChannels.value.filter(
                (sc) => !ticketStore.eventIds.includes(sc.id.toString()),
            );
        }
    };
    const $reset = () => {
        subscribedChannels.value = [];
    };

    watch(subscribedChannels, (newValue, oldValue) => {
        const delta = findChannelDelta(oldValue || [], newValue || []);
        if (delta.removed.length) unsubscribeChannels(delta.removed);
        if (delta.added.length) subscribeChannels(delta.added);
    });

    return {
        subscribedChannels,
        getSubscribedChannels,
        setSubscribedChannels,
        setSubscribedChannelsByEventIds,
        reset,
        $reset,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useWsStore, import.meta.hot));
}
